/* eslint-disable max-len */
import React, {
	useCallback,
	useEffect,
	useState,
} from 'react';
import { ColorResult } from '@uiw/react-color';
import styled from 'styled-components';
import ColorPicker from '../../right-panel/settings/color-pickers/color-picker';
import { useColorPickerPosition } from '../../right-panel/settings/color-pickers/util/use-color-picker-position';
import { StyledButton } from './debug-ui';

interface DebugColorToken {
	label: string;
	variable: string;
}

interface DebugColorTokenGroup {
	label: string;
	tokens: DebugColorToken[];
	columns?: number;
}

interface ColorPickerData {
	open: boolean;
	openHexAtStart: string;
	openToken: string;
}

const PICKER_WIDTH = 270;
const CARD_WIDTH = 200;
const COLUMNS_PER_ROW = 3;

const COLOR_TOKEN_GROUPS: DebugColorTokenGroup[] = [
	{
		label: 'Grays',
		tokens: [
			{ label: 'gray-100', variable: '--primitives-mode-1-gray-100' },
			{ label: 'gray-200', variable: '--primitives-mode-1-gray-200' },
			{ label: 'gray-300', variable: '--primitives-mode-1-gray-300' },
			{ label: 'gray-400', variable: '--primitives-mode-1-gray-400' },
			{ label: 'gray-450', variable: '--primitives-mode-1-gray-450' },
			{ label: 'gray-500', variable: '--primitives-mode-1-gray-500' },
			{ label: 'gray-550', variable: '--primitives-mode-1-gray-550' },
			{ label: 'gray-600', variable: '--primitives-mode-1-gray-600' },
			{ label: 'gray-650', variable: '--primitives-mode-1-gray-650' },
			{ label: 'gray-700', variable: '--primitives-mode-1-gray-700' },
			{ label: 'gray-800', variable: '--primitives-mode-1-gray-800' },
			{ label: 'gray-900', variable: '--primitives-mode-1-gray-900' },
			{ label: 'gray-1000', variable: '--primitives-mode-1-gray-1000' },
			{ label: 'gray-1100', variable: '--primitives-mode-1-gray-1100' },
			{ label: 'gray-1200', variable: '--primitives-mode-1-gray-1200' },
			{ label: 'gray-1300', variable: '--primitives-mode-1-gray-1300' },
		],
	},
	{
		label: 'Core tokens',
		tokens: [
			{ label: 'product-acc-400', variable: '--product-acc-400' },
			{ label: 'product-complim-400', variable: '--product-complim-400' },
			{ label: '--bg', variable: '--bg' },
		],
	},
	{
		label: 'Global Content',
		tokens: [
			{ label: 'global-cont-1', variable: '--global-cont-1' },
			{ label: 'global-cont-2', variable: '--global-cont-2' },
			{ label: 'global-cont-3', variable: '--global-cont-3' },
			{ label: 'global-cont-4', variable: '--global-cont-4' },
		],
	},
	{
		label: 'local-bttn-prim',
		columns: 2,
		tokens: [
			{ label: 'default bg', variable: '--local-bttn-prim-default-bg' },
			{ label: 'default icotext', variable: '--local-bttn-prim-default-icotext' },
			{ label: 'hover bg', variable: '--local-bttn-prim-hover-bg' },
			{ label: 'hover icotext', variable: '--local-bttn-prim-hover-icotext' },
			{ label: 'pressed bg', variable: '--local-bttn-prim-pressed-bg' },
			{ label: 'pressed icotext', variable: '--local-bttn-prim-pressed-icotext' },
		],
	},
	{
		label: 'local-bttn-sec',
		columns: 2,
		tokens: [
			{ label: 'default bg', variable: '--local-bttn-sec-default-bg' },
			{ label: 'default icotext', variable: '--local-bttn-sec-default-icotext' },
			{ label: 'hover bg', variable: '--local-bttn-sec-hover-bg' },
			{ label: 'hover icotext', variable: '--local-bttn-sec-hover-icotext' },
			{ label: 'pressed bg', variable: '--local-bttn-sec-pressed-bg' },
			{ label: 'pressed icotext', variable: '--local-bttn-sec-pressed-icotext' },
		],
	},
	{
		label: 'local-bttn-critical',
		columns: 2,
		tokens: [
			{ label: 'default bg', variable: '--local-bttn-critical-default-bg' },
			{ label: 'default icotext', variable: '--local-bttn-critical-default-icotext' },
			{ label: 'hover bg', variable: '--local-bttn-critical-hover-bg' },
			{ label: 'hover icotext', variable: '--local-bttn-critical-hover-icotext' },
			{ label: 'pressed bg', variable: '--local-bttn-critical-pressed-bg' },
			{ label: 'pressed icotext', variable: '--local-bttn-critical-pressed-icotext' },
		],
	},
	{
		label: 'local-link-sec',
		columns: 1,
		tokens: [
			{ label: 'default icotext', variable: '--local-link-sec-default-icotext' },
			{ label: 'hover icotext', variable: '--local-link-sec-hover-icotext' },
			{ label: 'pressed icotext', variable: '--local-link-sec-pressed-icotext' },
		],
	},
	{
		label: 'local-link-prim',
		columns: 1,
		tokens: [
			{ label: 'default icotext', variable: '--local-link-prim-default-icotext' },
			{ label: 'hover icotext', variable: '--local-link-prim-hover-icotext' },
			{ label: 'pressed icotext', variable: '--local-link-prim-pressed-icotext' },
		],
	},
];

const COLOR_TOKENS: DebugColorToken[] = COLOR_TOKEN_GROUPS.flatMap((group: DebugColorTokenGroup) => group.tokens);

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding-top: 16px;

	h5 {
		color: var(--primitives-mode-1-gray-200) !important;
	}
`;

const AccordionControls = styled.div`
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
`;

const TokenGroupsWrap = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	gap: 16px;
`;

const TokenGroup = styled.div`
	display: flex;
	flex: 0 1 auto;
	flex-direction: column;
	gap: 8px;
	max-width: 100%;
	border: 1px solid #3c3a42;
	border-radius: 8px;
	padding: 8px;
	background: #121114;
`;

const TokenGroupTitle = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
	color: #8ff7ff;
	font-size: 13px;
	font-weight: 700;
	margin: 0 !important;
	padding: 0 !important;
	border: 0;
	background: transparent;
	cursor: pointer;
	text-align: left;
`;

const AccordionIcon = styled.span`
	color: var(--primitives-mode-1-gray-200);
	font-size: 12px;
`;

const TokenGrid = styled.div<{ $columns?: number }>`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	max-width: ${({ $columns = 2 }) => `${($columns * CARD_WIDTH) + (($columns - 1) * 8)}px`};
`;

const TokenCard = styled.div<{ $selected: boolean; $columns?: number }>`
	display: grid;
	grid-template-columns: 28px minmax(0, 1fr);
	align-items: center;
	gap: 8px;
	border: 1px solid ${({ $selected }) => $selected ? 'var(--primitives-mode-1-gray-200)' : '#4f4c57'};
	border-radius: 8px;
	padding: 8px;
	background: #09090a;
	flex: 0 1 ${({ $columns = 2 }) => $columns === 1 ? `${CARD_WIDTH}px` : 'calc((100% - 8px) / 2)'};
	max-width: ${CARD_WIDTH}px;
`;

const ColorPickerButton = styled.button`
	width: 28px;
	height: 28px;
	padding: 0 !important;
	margin: 0 !important;
	border: 1px solid #625e70;
	border-radius: 6px;
	background: transparent;
	cursor: pointer;
`;

const TokenName = styled.div`
	padding: 0 !important;
	margin: 0 !important;
	border: 0;
	background: transparent;
	color: var(--primitives-mode-1-gray-200);
	font-weight: 700;
	text-align: left;
	cursor: pointer;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const HelpText = styled.p`
	margin: 0;
	color: #bfbbcf;
	font-size: 12px;
	line-height: 1.4;
`;

const normalizeTokenVariable = (variable: string): string => variable.startsWith('--') ? variable : `--${variable}`;

const rgbToHex = (value: string): string | null => {
	const match = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
	if (match === null) return null;

	const red = Number(match[1]).toString(16).padStart(2, '0');
	const green = Number(match[2]).toString(16).padStart(2, '0');
	const blue = Number(match[3]).toString(16).padStart(2, '0');

	return `#${red}${green}${blue}`;
};

const normalizeHexColor = (value: string): string | null => {
	if (/^#[0-9a-f]{6}$/i.test(value)) return value;
	if (/^#[0-9a-f]{3}$/i.test(value)) return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;

	return rgbToHex(value);
};

let colorProbe: HTMLSpanElement | null = null;

const getColorProbe = (): HTMLSpanElement => {
	if (colorProbe !== null) return colorProbe;

	colorProbe = document.createElement('span');
	colorProbe.style.display = 'none';
	document.body.appendChild(colorProbe);

	return colorProbe;
};

const resolveCssColorToHex = (value: string): string | null => {
	if (!value) return null;

	const normalizedColor = normalizeHexColor(value);
	if (normalizedColor !== null) return normalizedColor;

	const probe = getColorProbe();
	probe.style.color = '';
	probe.style.color = value;
	if (!probe.style.color) return null;

	return normalizeHexColor(getComputedStyle(probe).color);
};

const resolveTokenToHex = (variable: string): string => {
	const normalizedVariable = normalizeTokenVariable(variable);
	const rootValue = getComputedStyle(document.documentElement).getPropertyValue(normalizedVariable).trim();
	return resolveCssColorToHex(rootValue) ?? resolveCssColorToHex(`var(${normalizedVariable})`) ?? '#000000';
};

const getTokenIndex = (variable: string): number => COLOR_TOKENS.findIndex((token: DebugColorToken) => token.variable === variable);

export default function DebugColorTokens() {
	const { containerRef, positionPicker } = useColorPickerPosition({
		pickerWidth: PICKER_WIDTH,
		offsetY: -54,
		offsetX: 28,
		strategy: 'auto',
	});
	const [values, setValues] = useState<Record<string, string>>({});
	const [selectedToken, setSelectedToken] = useState<string>(COLOR_TOKENS[0].variable);
	const [colorPickerData, setColorPickerData] = useState<ColorPickerData>({
		open: false,
		openHexAtStart: '#000000',
		openToken: '',
	});
	const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

	const refreshValues = useCallback(() => {
		const nextValues: Record<string, string> = {};

		COLOR_TOKENS.forEach((token: DebugColorToken) => {
			nextValues[token.variable] = resolveTokenToHex(token.variable);
		});

		setValues(nextValues);
	}, []);

	useEffect(() => {
		refreshValues();
	}, [refreshValues]);

	const setTokenValue = useCallback((variable: string, value: string) => {
		document.documentElement.style.setProperty(normalizeTokenVariable(variable), value);
		refreshValues();
	}, [refreshValues]);

	const handleOpenColorPicker = useCallback((domRect: DOMRect, token: DebugColorToken, columnsPerRow: number) => {
		positionPicker(domRect, getTokenIndex(token.variable), columnsPerRow);
		setSelectedToken(token.variable);
		setColorPickerData({
			open: true,
			openHexAtStart: values[token.variable] ?? '#000000',
			openToken: token.variable,
		});
	}, [positionPicker, values]);

	const handleColorChange = useCallback((color: ColorResult) => {
		if (!colorPickerData.openToken) return;
		setTokenValue(colorPickerData.openToken, color.hex);
		setColorPickerData((current: ColorPickerData) => ({
			...current,
			openHexAtStart: color.hex,
		}));
	}, [colorPickerData.openToken, setTokenValue]);

	const handleCloseColorPicker = useCallback(() => {
		setColorPickerData((current: ColorPickerData) => ({
			...current,
			open: false,
			openToken: '',
		}));
	}, []);

	const toggleGroup = useCallback((groupLabel: string) => {
		handleCloseColorPicker();
		setExpandedGroups((current: Record<string, boolean>) => ({
			...current,
			[groupLabel]: !current[groupLabel],
		}));
	}, [handleCloseColorPicker]);

	const expandAllGroups = useCallback(() => {
		const nextExpandedGroups: Record<string, boolean> = {};
		COLOR_TOKEN_GROUPS.forEach((group: DebugColorTokenGroup) => {
			nextExpandedGroups[group.label] = true;
		});
		setExpandedGroups(nextExpandedGroups);
	}, []);

	const collapseAllGroups = useCallback(() => {
		handleCloseColorPicker();
		setExpandedGroups({});
	}, [handleCloseColorPicker]);


	return (
		<Wrapper className="section">
			<HelpText>
				Edit CSS variables live on the document root. Changes are temporary and reset on refresh unless copied into the theme styles.
			</HelpText>
			<AccordionControls className='flex-align-center'>
				<StyledButton type="button" onClick={expandAllGroups}>Expand all</StyledButton>
				<StyledButton type="button" onClick={collapseAllGroups}>Collapse all</StyledButton>
			</AccordionControls>
			<TokenGroupsWrap>
				{COLOR_TOKEN_GROUPS.map((group: DebugColorTokenGroup) => {
					const isExpanded = expandedGroups[group.label] === true;

					return (
						<TokenGroup key={group.label}>
							<TokenGroupTitle
								type="button"
								onClick={() => toggleGroup(group.label)}
								aria-expanded={isExpanded}
								className='transparent'
							>
								<span>{group.label}</span>
								<AccordionIcon>{isExpanded ? '▾' : '▸'}</AccordionIcon>
							</TokenGroupTitle>
							{isExpanded && (
								<TokenGrid $columns={group.columns}>
									{group.tokens.map((token: DebugColorToken) => {
										const currentValue = values[token.variable] ?? '#000000';
										const isSelected = selectedToken === token.variable;

										return (
											<TokenCard key={token.variable} $selected={isSelected} $columns={group.columns}>
												<ColorPickerButton
													style={{ backgroundColor: currentValue }}
													type="button"
													onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleOpenColorPicker(event.currentTarget.getBoundingClientRect(), token, group.columns ?? COLUMNS_PER_ROW)}
												/>
												<TokenName onClick={() => setSelectedToken(token.variable)} title={token.variable} className='bl-font-l2'>
													{token.label}
												</TokenName>
											</TokenCard>
										);
									})}
								</TokenGrid>
							)}
						</TokenGroup>
					);
				})}
			</TokenGroupsWrap>
			<div style={{ position: 'fixed' }} ref={containerRef} id="debug-color-token-picker-portal" className="color-portal"/>
			<ColorPicker
				open={colorPickerData.open}
				openHexAtStart={colorPickerData.openHexAtStart}
				onChange={handleColorChange}
				onClose={handleCloseColorPicker}
				portalId="debug-color-token-picker-portal"
				pickerWidth={PICKER_WIDTH}
				pickerClassname="color-picker-darker"
			/>
		</Wrapper>
	);
}
